import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { HardHat, Send, Loader2, X, Paperclip, Image as ImageIcon, Trash2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "./ConfirmProvider";
import { compressImage } from "../lib/imageCompression";

/**
 * Standalone composer for posting a task update (note + optional photos).
 * Extracted so pages like ContractorProject can position it independently of
 * the updates timeline (e.g. above the "Mark task completed" button).
 */
export function TaskUpdateComposer({ taskId, onPosted, placeholder }) {
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const pickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length + files.length > 5) {
      toast.error("Max 5 images per update");
      return;
    }
    setFiles((prev) => [...prev, ...list]);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!note.trim() && files.length === 0) {
      toast.error("Add a note or an image before posting");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("note", note.trim());
      const compressed = await Promise.all(files.map((f) => compressImage(f)));
      compressed.forEach((f) => fd.append("files", f));
      await api.post(`/tasks/${taskId}/contractor-updates`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Update posted");
      setNote("");
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      onPosted && (await onPosted());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-[#EBE5D9] bg-white p-3" data-testid="task-update-composer">
      <textarea
        className="input-soft text-sm min-h-[60px]"
        placeholder={placeholder || "Post a progress update or note (e.g. Cabinet install 60% done, tempered glass delayed by 2 days)."}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        data-testid="task-update-note"
      />
      {files.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#F7F4EC] text-xs text-[#2C2A29]">
              <ImageIcon size={11} />
              <span className="truncate max-w-[160px]">{f.name}</span>
              <button
                type="button"
                onClick={() => setFiles(files.filter((_, j) => j !== i))}
                className="text-[#9F1239] hover:opacity-80"
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between mt-2">
        <label className="btn-ghost text-xs inline-flex items-center gap-1 cursor-pointer">
          <Paperclip size={12} /> Attach photo{files.length > 0 ? ` (${files.length})` : ""}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={pickFiles}
          />
        </label>
        <button
          type="submit"
          disabled={saving || (!note.trim() && files.length === 0)}
          data-testid="task-update-submit"
          className="btn-primary text-sm inline-flex items-center gap-1.5"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          {saving ? "Posting…" : "Post update"}
        </button>
      </div>
    </form>
  );
}

/**
 * Displays contractor updates for a task inside the expanded task drawer.
 * - Any project member (staff or contractor) can post an update; images optional (≤5, ≤5MB each).
 * - Author or staff can delete their entry.
 */
export default function TaskContractorUpdates({ taskId, autoLoadWhen, isCustomer = false, hideComposer = false, refreshKey = 0 }) {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tasks/${taskId}/contractor-updates`);
      setUpdates(data.updates || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    if (autoLoadWhen) load();
  }, [autoLoadWhen, load, refreshKey]);

  const removeUpdate = async (u) => {
    const ok = await confirm({
      title: "Delete update?",
      message: "This will remove the note and any attached images.",
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/tasks/contractor-updates/${u.update_id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleImageVisibility = async (updateId, imageId, next) => {
    try {
      await api.patch(`/tasks/contractor-updates/${updateId}/images/${imageId}/visibility`, {
        hidden_from_customer: next,
      });
      // Optimistic local update
      setUpdates((prev) =>
        prev.map((u) =>
          u.update_id !== updateId
            ? u
            : {
                ...u,
                images: (u.images || []).map((img) =>
                  img.image_id === imageId ? { ...img, hidden_from_customer: next } : img
                ),
              }
        )
      );
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const formatWhen = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <div className="border-t border-[#EBE5D9] pt-3 mt-2" data-testid="task-contractor-updates">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-[#7A7571]">
          <HardHat size={13} /> Contractor updates
          {updates.length > 0 && (
            <span className="ml-1 px-1.5 rounded-full bg-[#F7F4EC] text-[#2C2A29] text-[10px] font-semibold">
              {updates.length}
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-[#7A7571] py-3 text-center">
          <Loader2 size={14} className="animate-spin inline" /> Loading…
        </div>
      ) : updates.length === 0 ? (
        <div className="text-xs text-[#7A7571] italic py-2">
          No updates yet. Once the contractor starts, their notes and site photos will show here.
        </div>
      ) : (
        <div className="space-y-2 mb-3">
          {updates.map((u) => (
            <div
              key={u.update_id}
              className="rounded-2xl border border-[#EBE5D9] bg-[#FBF7EF] p-3"
              data-testid={`contractor-update-${u.update_id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center flex-wrap gap-1.5 text-xs text-[#7A7571]">
                    <span className="font-semibold text-[#2C2A29]">{u.created_by_name}</span>
                    <span className="text-[#B8B0A6]">·</span>
                    <span>{formatWhen(u.created_at)}</span>
                    {u.status_hint && (() => {
                      const map = {
                        in_progress:       { text: "In progress",     cls: "bg-white border border-[#EBE5D9] text-[#2C2A29]" },
                        blocked:           { text: "Blocked",         cls: "bg-[#FEE2E2] border border-[#FCA5A5] text-[#7F1D1D]" },
                        done:              { text: "Done",            cls: "bg-white border border-[#EBE5D9] text-[#2C2A29]" },
                        rework:            { text: "Rework requested", cls: "bg-[#FFEDD5] border border-[#FDBA74] text-[#9A3412]" },
                        approved:          { text: "Approved",        cls: "bg-[#DCFCE7] border border-[#86EFAC] text-[#166534]" },
                        customer_approved: { text: "Homeowner approved", cls: "bg-[#DBF4E5] border border-[#86EFAC] text-[#047857]" },
                        customer_rework:   { text: "Homeowner rework",  cls: "bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B]" },
                      };
                      const meta = map[u.status_hint] || { text: u.status_hint, cls: "bg-white border border-[#EBE5D9] text-[#2C2A29]" };
                      return (
                        <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${meta.cls}`}>
                          {meta.text}
                        </span>
                      );
                    })()}
                  </div>
                  {u.note && (
                    <div className="mt-1.5 text-sm text-[#2C2A29] whitespace-pre-wrap leading-relaxed">
                      {u.note}
                    </div>
                  )}
                  {u.images?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {u.images.map((img) => {
                        const src = `${api.defaults.baseURL}/tasks/contractor-updates/${u.update_id}/images/${img.image_id}`;
                        const hidden = !!img.hidden_from_customer;
                        const canToggle = !isCustomer && ["admin", "pm", "designer"].includes(user?.role);
                        return (
                          <div
                            key={img.image_id}
                            className={`relative w-20 h-20 rounded-xl overflow-hidden bg-white border ${
                              hidden ? "border-[#F87171] ring-1 ring-[#F87171]" : "border-[#EBE5D9]"
                            } group`}
                            data-testid={`update-image-${img.image_id}`}
                          >
                            <a href={src} target="_blank" rel="noreferrer" className="block w-full h-full">
                              <img
                                src={src}
                                alt="Update"
                                loading="lazy"
                                className={`w-full h-full object-cover ${hidden ? "opacity-60" : ""}`}
                              />
                            </a>
                            {hidden && (
                              <div className="pointer-events-none absolute bottom-0 left-0 right-0 px-1 py-0.5 text-[9px] font-semibold text-white bg-[#9F1239] uppercase tracking-wider text-center">
                                Hidden
                              </div>
                            )}
                            {canToggle && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  toggleImageVisibility(u.update_id, img.image_id, !hidden);
                                }}
                                title={hidden ? "Show to customer" : "Hide from customer"}
                                data-testid={`update-image-${img.image_id}-toggle`}
                                className="absolute top-1 right-1 p-1 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                {hidden ? <EyeOff size={11} /> : <Eye size={11} />}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {(user?.user_id === u.created_by || ["admin", "pm"].includes(user?.role)) && (
                  <button
                    onClick={() => removeUpdate(u)}
                    className="btn-ghost !p-1.5 text-[#9F1239] flex-shrink-0"
                    title="Delete update"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Composer — inline for convenience unless the caller renders it elsewhere */}
      {!hideComposer && !isCustomer && (
        <TaskUpdateComposer taskId={taskId} onPosted={load} />
      )}
    </div>
  );
}
